/**
 * GRANULLO PHASE 2 — Authority Matrix Context
 * State management for decision authority distribution analysis
 */

import React, { createContext, useContext, useState, useMemo } from 'react';

// Authority Tiers
const AUTHORITY_TIERS = {
  EXECUTIVE: { tier: 1, name: 'Executive Decision Authority', color: '#a371f7', description: 'Strategic commitment approval' },
  OPERATIONAL: { tier: 2, name: 'Operational Authority', color: '#58a6ff', description: 'Operational commitment management' },
  EXECUTION: { tier: 3, name: 'Execution Authority', color: '#3fb950', description: 'Direct commitment execution' }
};

// Sample Authority Data
const SAMPLE_AUTHORITY_NODES = [
  {
    id: 'auth-ceo',
    commitmentId: 'commit-strategy-001',
    authorityTier: 1,
    authorityOwner: { id: 'owner-1', name: 'Chief Executive', role: 'CEO', organization: 'Parent Corp' },
    authorityType: 'executive',
    dependencies: ['auth-cfo', 'auth-coo'],
    delegatedTo: ['auth-cfo', 'auth-coo']
  },
  {
    id: 'auth-cfo',
    commitmentId: 'commit-finance-001',
    authorityTier: 2,
    authorityOwner: { id: 'owner-2', name: 'Chief Financial Officer', role: 'CFO', organization: 'Parent Corp' },
    authorityType: 'operational',
    dependencies: ['auth-treasury', 'auth-compliance'],
    delegatedFrom: 'auth-ceo',
    delegatedTo: ['auth-treasury', 'auth-compliance']
  },
  {
    id: 'auth-coo',
    commitmentId: 'commit-ops-001',
    authorityTier: 2,
    authorityOwner: { id: 'owner-3', name: 'Chief Operating Officer', role: 'COO', organization: 'Parent Corp' },
    authorityType: 'operational',
    dependencies: ['auth-supply', 'auth-logistics'],
    delegatedFrom: 'auth-ceo',
    delegatedTo: ['auth-supply', 'auth-logistics']
  },
  {
    id: 'auth-treasury',
    commitmentId: 'commit-treasury-001',
    authorityTier: 3,
    authorityOwner: { id: 'owner-4', name: 'Treasury Manager', role: 'Manager', organization: 'Finance Division' },
    authorityType: 'execution',
    dependencies: [],
    delegatedFrom: 'auth-cfo'
  },
  {
    id: 'auth-compliance',
    commitmentId: 'commit-compliance-001',
    authorityTier: 3,
    authorityOwner: { id: 'owner-5', name: 'Compliance Officer', role: 'Officer', organization: 'Finance Division' },
    authorityType: 'execution',
    dependencies: [],
    delegatedFrom: 'auth-cfo'
  },
  {
    id: 'auth-supply',
    commitmentId: 'commit-supply-001',
    authorityTier: 3,
    authorityOwner: { id: 'owner-6', name: 'Supply Chain Manager', role: 'Manager', organization: 'Operations Division' },
    authorityType: 'execution',
    dependencies: [],
    delegatedFrom: 'auth-coo'
  },
  {
    id: 'auth-logistics',
    commitmentId: 'commit-logistics-001',
    authorityTier: 3,
    authorityOwner: { id: 'owner-7', name: 'Logistics Coordinator', role: 'Coordinator', organization: 'Operations Division' },
    authorityType: 'execution',
    dependencies: [],
    delegatedFrom: 'auth-coo'
  }
];

const AuthorityMatrixContext = createContext(null);

export function AuthorityMatrixProvider({ children }) {
  const [authorityNodes, setAuthorityNodes] = useState(SAMPLE_AUTHORITY_NODES);
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewMode, setViewMode] = useState('hierarchy'); // 'hierarchy' | 'network' | 'concentration'
  
  // Calculate Authority Metrics
  const authorityMetrics = useMemo(() => {
    // Concentration Index - % of commitments under single owner
    const ownerCounts = {};
    authorityNodes.forEach(node => {
      const ownerId = node.authorityOwner.id;
      ownerCounts[ownerId] = (ownerCounts[ownerId] || 0) + 1;
    });
    const maxOwnerCount = Math.max(...Object.values(ownerCounts));
    const concentrationIndex = Math.round((maxOwnerCount / authorityNodes.length) * 100);
    
    // Bottleneck Score - nodes with many dependencies waiting
    const bottleneckNodes = authorityNodes.filter(node => 
      node.delegatedTo && node.delegatedTo.length > 2
    );
    const bottleneckScore = Math.round((bottleneckNodes.length / authorityNodes.length) * 100);
    
    // Dependency Depth - max delegation chain length
    const calculateDepth = (nodeId, visited = new Set()) => {
      if (visited.has(nodeId)) return 0;
      visited.add(nodeId);
      const node = authorityNodes.find(n => n.id === nodeId);
      if (!node || !node.delegatedTo || node.delegatedTo.length === 0) return 1;
      return 1 + Math.max(...node.delegatedTo.map(id => calculateDepth(id, visited)));
    };
    const dependencyDepth = Math.max(...authorityNodes.map(n => calculateDepth(n.id)));
    
    // Fragmentation Index - commitments without clear owner
    const uncommittedAuthority = authorityNodes.filter(node => 
      !node.authorityOwner || !node.authorityOwner.name
    );
    const fragmentationIndex = uncommittedAuthority.length / authorityNodes.length;
    
    // Tier distribution
    const tierDistribution = {
      executive: authorityNodes.filter(n => n.authorityTier === 1).length,
      operational: authorityNodes.filter(n => n.authorityTier === 2).length,
      execution: authorityNodes.filter(n => n.authorityTier === 3).length
    };
    
    return {
      concentrationIndex,
      bottleneckScore,
      dependencyDepth,
      fragmentationIndex,
      tierDistribution,
      totalNodes: authorityNodes.length,
      bottleneckCount: bottleneckNodes.length
    };
  }, [authorityNodes]);
  
  // Get authority chain for a node
  const getAuthorityChain = (nodeId) => {
    const chain = [];
    let currentId = nodeId;
    const visited = new Set();
    
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const node = authorityNodes.find(n => n.id === currentId);
      if (node) {
        chain.unshift(node);
        currentId = node.delegatedFrom;
      } else {
        break;
      }
    }
    
    return chain;
  };
  
  // Get downstream authority
  const getDownstreamAuthority = (nodeId) => {
    const downstream = [];
    const queue = [nodeId];
    const visited = new Set();
    
    while (queue.length > 0) {
      const currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      
      const node = authorityNodes.find(n => n.id === currentId);
      if (node && node.id !== nodeId) {
        downstream.push(node);
      }
      
      if (node?.delegatedTo) {
        queue.push(...node.delegatedTo);
      }
    }
    
    return downstream;
  };
  
  const value = {
    authorityNodes,
    setAuthorityNodes,
    selectedNode,
    setSelectedNode,
    viewMode,
    setViewMode,
    authorityMetrics,
    getAuthorityChain,
    getDownstreamAuthority,
    AUTHORITY_TIERS
  };
  
  return (
    <AuthorityMatrixContext.Provider value={value}>
      {children}
    </AuthorityMatrixContext.Provider>
  );
}

export function useAuthorityMatrix() {
  const context = useContext(AuthorityMatrixContext);
  if (!context) {
    throw new Error('useAuthorityMatrix must be used within AuthorityMatrixProvider');
  }
  return context;
}

export { AUTHORITY_TIERS };
export default AuthorityMatrixContext;
